import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, BookOpen, Clock, LifeBuoy, Newspaper } from "lucide-react";
import Seo, { organizationLd } from "../components/Seo";
import SunLogo, { PlayGlyph } from "../components/SunLogo";
import SocialLinks from "../components/SocialLinks";
import { site } from "../mock";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export default function Links() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`, { params: { limit: 6 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  const [latest, ...rest] = items;

  return (
    <>
      <Seo
        title="InZoRAH — every link in one place"
        description="The latest ingredient answers, the InZoRAH app and the A-Z ingredient library, all in one place."
        path="/links"
        noindex
        jsonLd={organizationLd}
      />
      <main className="lk-shell" data-testid="links-page">
        <header className="lk-top">
          <SunLogo />
          <h1 className="lk-brand">{site.brand}</h1>
          <p className="lk-handle">@inzorah</p>
          <p className="lk-tag">
            We read the ingredient label so you don&rsquo;t have to.
          </p>
        </header>

        {latest && (
          <a
            className="lk-latest"
            href={`/ingredient/${latest.slug}`}
            data-testid="links-latest"
            onClick={() => track("click_links_latest", { label: latest.slug })}
          >
            <span className="lk-eyebrow">Newest answer</span>
            <span className="lk-latest-title">{latest.title}</span>
            <span className="lk-latest-meta">
              <Clock size={12} aria-hidden="true" /> {latest.read_time} min ·{" "}
              {fmtDate(latest.published_at)}
            </span>
            <span className="lk-go">
              Read it <ArrowUpRight size={15} aria-hidden="true" />
            </span>
          </a>
        )}

        <a
          className="lk-cta"
          href={site.playUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="links-play"
          onClick={() => track("click_store", { label: "links page google play" })}
        >
          <PlayGlyph />
          <span>
            <b>Scan any label free</b>
            <em>Get InZoRAH on Google Play</em>
          </span>
          <ArrowUpRight size={16} aria-hidden="true" />
        </a>

        {rest.length > 0 && (
          <section className="lk-group" data-testid="links-recent">
            <h2 className="lk-h2">More ingredient answers</h2>
            {rest.map((p) => (
              <a
                key={p.slug}
                className="lk-row"
                href={`/ingredient/${p.slug}`}
                onClick={() => track("click_links_post", { label: p.slug })}
              >
                <span>{p.title}</span>
                <ArrowUpRight size={15} aria-hidden="true" />
              </a>
            ))}
          </section>
        )}

        <section className="lk-group">
          <h2 className="lk-h2">Look something up</h2>
          <Link
            className="lk-row"
            to="/ingredients"
            data-testid="links-glossary"
            onClick={() => track("click_ingredients_az", { label: "links page" })}
          >
            <span>
              <BookOpen size={14} aria-hidden="true" /> Ingredient A-Z library
            </span>
            <ArrowUpRight size={15} aria-hidden="true" />
          </Link>
          <Link
            className="lk-row"
            to="/ingredient"
            onClick={() => track("click_ingredient_blog", { label: "links page" })}
          >
            <span>
              <Newspaper size={14} aria-hidden="true" /> The ingredient blog
            </span>
            <ArrowUpRight size={15} aria-hidden="true" />
          </Link>
          <a
            className="lk-row"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("click_support", { label: "links page" })}
          >
            <span>
              <LifeBuoy size={14} aria-hidden="true" /> Support portal
            </span>
            <ArrowUpRight size={15} aria-hidden="true" />
          </a>
        </section>

        <SocialLinks />

        <footer className="lk-foot">
          <span>
            KYSolutions LLC · <Link to="/contact">Contact</Link> ·{" "}
            <Link to="/legal">Legal</Link>
          </span>
          <span>Educational information, not medical advice.</span>
        </footer>
      </main>
    </>
  );
}
